/** Kudos badges from the API (admin-configurable), shared by the feed, the give-kudos form and the admin page. */
import { useQuery } from '@tanstack/react-query';
import type { KudosBadgeDef } from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { positionTone } from '@/lib/color';
import { useWorkspaceId } from '@/lib/hooks';

export const badgesKey = (ws: string) => ['kudos-badges', ws] as const;
export function useKudosBadges() {
  const ws = useWorkspaceId();
  const q = useQuery({ queryKey: badgesKey(ws ?? ''), queryFn: () => api.kudosBadges(ws!), enabled: Boolean(ws), staleTime: 5 * 60_000 });
  const all = q.data?.badges ?? [];
  return { ...q, all, byKey: new Map(all.map(b => [b.key, b])), pickable: all.filter(b => !b.system && !b.retired_at), canManage: Boolean(q.data?.can_manage) };
}

export function BadgeChip({ badge, className = '' }: { badge: Pick<KudosBadgeDef, 'name' | 'emoji' | 'color'> & { retired_at?: string | null }; className?: string }) {
  // Admin colour kept as a small dot; the name is plain muted text, not a filled pill (t344u).
  const t = positionTone(badge.color);
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 text-xs font-medium text-slate-600 dark:text-slate-300 ${className}`}
      title={badge.retired_at ? `${badge.name} (retired)` : badge.name} data-testid="badge-chip">
      <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: badge.color || t.border }} />
      <span aria-hidden>{badge.emoji}</span>{badge.name}
    </span>
  );
}
